/**
 * Wrapper RevenueCat per gestione IAP.
 *
 * Design:
 *  - Su web/PWA: tutti i metodi sono no-op o simulazione (l'app web non
 *    ha IAP, l'utente deve scaricare l'app mobile per acquistare)
 *  - Su Capacitor nativo: chiama RevenueCat se le API key sono configurate;
 *    altrimenti fallback a simulazione (utile per dev sul simulator senza
 *    aver configurato gli account store)
 *
 * Configurazione API key:
 *  Imposta in `.env.production`:
 *    VITE_REVENUECAT_API_KEY_IOS=appl_xxxxx
 *    VITE_REVENUECAT_API_KEY_ANDROID=goog_xxxxx
 *  Le trovi su https://app.revenuecat.com → Project Settings → API Keys
 *
 * Product ID convenzione:
 *   itinere_unlock_all (Non-Consumable)
 *   Entitlement: unlock_all
 */

const ENTITLEMENT_ID = "unlock_all";
const PRODUCT_ID = "itinere_unlock_all";

const API_KEY_IOS =
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_REVENUECAT_API_KEY_IOS) || "";
const API_KEY_ANDROID =
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_REVENUECAT_API_KEY_ANDROID) || "";

let initialized = false;
let initPromise: Promise<void> | null = null;

interface NativeCapacitor {
  isNativePlatform: () => boolean;
  getPlatform: () => string;
}

async function getCapacitor(): Promise<NativeCapacitor | null> {
  try {
    // @ts-ignore
    const mod = await import("@capacitor/core");
    return mod.Capacitor as NativeCapacitor;
  } catch {
    return null;
  }
}

async function getPurchasesModule() {
  try {
    // @ts-ignore
    return await import("@revenuecat/purchases-capacitor");
  } catch {
    return null;
  }
}

/**
 * Inizializza RevenueCat. Chiamata UNA VOLTA al boot dell'app native.
 * No-op su web. Safe per re-call (singleton).
 */
export async function initIap(): Promise<void> {
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const Cap = await getCapacitor();
    if (!Cap || !Cap.isNativePlatform()) {
      // Web: nulla da inizializzare. L'app web non supporta IAP nativo.
      initialized = true;
      return;
    }

    const platform = Cap.getPlatform();
    const apiKey = platform === "ios" ? API_KEY_IOS : API_KEY_ANDROID;
    if (!apiKey) {
      console.warn(`[IAP] RevenueCat API key per ${platform} non configurata. IAP in modalita' simulazione.`);
      initialized = true;
      return;
    }

    const Purchases = await getPurchasesModule();
    if (!Purchases) {
      console.warn("[IAP] Modulo @revenuecat/purchases-capacitor non disponibile.");
      initialized = true;
      return;
    }

    try {
      await Purchases.Purchases.configure({ apiKey });
      initialized = true;
      console.log(`[IAP] RevenueCat inizializzato per ${platform}`);
    } catch (e) {
      console.error("[IAP] Configure failed:", e);
      initialized = true;
    }
  })();

  return initPromise;
}

/**
 * Verifica se l'utente ha gia' acquistato il bundle. Si chiama dopo init
 * per sincronizzare lo stato locale (purchasedAll) con la verita' della
 * piattaforma store (utile per restore automatico al boot).
 */
export async function isUnlocked(): Promise<boolean> {
  await initIap();
  const Cap = await getCapacitor();
  if (!Cap || !Cap.isNativePlatform()) return false;

  const Purchases = await getPurchasesModule();
  if (!Purchases) return false;

  try {
    const { customerInfo } = await Purchases.Purchases.getCustomerInfo();
    return !!customerInfo.entitlements.active[ENTITLEMENT_ID];
  } catch (e) {
    console.warn("[IAP] getCustomerInfo failed:", e);
    return false;
  }
}

/**
 * Avvia il flusso d'acquisto nativo. Apre lo sheet App Store / Play Store
 * con il prezzo localizzato + Touch ID/Face ID per confermare.
 *
 * Ritorna true se acquisto andato a buon fine, false se annullato.
 * Throws su errori (rete, payment failed, ecc.).
 */
export async function purchaseUnlockAll(): Promise<boolean> {
  await initIap();
  const Cap = await getCapacitor();
  if (!Cap || !Cap.isNativePlatform()) {
    // Web: simulazione per dev
    await new Promise(r => setTimeout(r, 700));
    return true;
  }

  const Purchases = await getPurchasesModule();
  if (!Purchases) {
    // Native ma RevenueCat non disponibile: fallback simulazione
    await new Promise(r => setTimeout(r, 700));
    return true;
  }

  try {
    // 1. Recupera l'offerta corrente da RevenueCat (cosi' usiamo il
    //    "default offering" configurato dashboard-side, senza hardcoded ID)
    const offerings = await Purchases.Purchases.getOfferings();
    const currentOffering = offerings.current;
    if (!currentOffering || currentOffering.availablePackages.length === 0) {
      throw new Error("Nessuna offerta disponibile (configura il default offering su RevenueCat)");
    }
    // Prendi il primo package (corrisponde al product itinere_unlock_all)
    const pkg = currentOffering.availablePackages.find(
      (p: any) => p.identifier === PRODUCT_ID || p.product.identifier === PRODUCT_ID
    ) || currentOffering.availablePackages[0];

    // 2. Acquista
    const result = await Purchases.Purchases.purchasePackage({ aPackage: pkg });
    return !!result.customerInfo.entitlements.active[ENTITLEMENT_ID];
  } catch (e: any) {
    if (e?.userCancelled || e?.code === "PURCHASE_CANCELLED") {
      // Annullamento esplicito: non e' un errore
      return false;
    }
    console.error("[IAP] Purchase failed:", e);
    throw e;
  }
}

/**
 * Restore purchases: utile per utenti che hanno gia' acquistato e
 * cambiano device o reinstallano l'app. Apple lo richiede esplicitamente
 * come bottone in app.
 */
export async function restoreUnlockAll(): Promise<boolean> {
  await initIap();
  const Cap = await getCapacitor();
  if (!Cap || !Cap.isNativePlatform()) {
    // Web: nessun acquisto da ripristinare
    return false;
  }

  const Purchases = await getPurchasesModule();
  if (!Purchases) return false;

  try {
    const result = await Purchases.Purchases.restorePurchases();
    return !!result.customerInfo.entitlements.active[ENTITLEMENT_ID];
  } catch (e) {
    console.error("[IAP] Restore failed:", e);
    return false;
  }
}
